// LeadActivity.jsx

import React from "react";
import Button from "../ui/Button";

import {
  formatDateTime,
  splitBudgetIntoValueAndUnit,
} from "../../hooks/commonHooks";

import { useNavigate } from "react-router-dom";

import {
  Flame,
  MapPin,
  Languages,
  IndianRupee,
  Activity,
  Clock3,
  MessageSquare,
  Building2,
  Eye,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";

import Card from "../ui/Card";
import SectionCard from "../ui/SectionCard";
import ScoreBadge from "./ScoreBadge";
import StatCard from "../ui/StatCard";

import {
  formatDuration,
  generateLeadAnalytics,
} from "../../hooks/activityCalulations";

import PageContainer from "../common/PageContainer";

import { useLeadStore } from "../../store/leadStore";

import { useAuthStore } from "../../store/authStore";

import LeadBasicInfo from "./LeadBasicInfo";

import "../../styles/lead_activity.css";

export default function LeadActivity() {
  const { role } = useAuthStore();
  const isAdmin = role === "admin";
  const navigate = useNavigate();
  const { selectedLead } = useLeadStore();

  if (!isAdmin) {
    return null;
  }

  const activitySummary =
    selectedLead?.activity_summary ?? "No activity summary available";
  const analytics = generateLeadAnalytics(selectedLead?.chat_activity || []);
  const propertyInterest = analytics.propertyInterest || [];
  const languageMonitoring = selectedLead?.language_monitoring;

  const formatBudget = (budget) => {
    if (!budget) return "Not Selected";

    const splbudget_min = splitBudgetIntoValueAndUnit(budget.min);
    const splbudget_max = splitBudgetIntoValueAndUnit(budget.max);

    return `₹${splbudget_min.value}${splbudget_min.unit} - ₹${splbudget_max.value}${splbudget_max.unit}`;
  };

  const category = selectedLead?.category?.toLowerCase();

  let leadTemperatureClass = "";

  if (category === "hot") {
    leadTemperatureClass = "hot-lead";
  } else if (category === "warm") {
    leadTemperatureClass = "warm-lead";
  } else if (category === "cold") {
    leadTemperatureClass = "cold-lead";
  }

  return (
    <PageContainer>
      <div className="lead-activity-page d-flex flex-column gap-2 mx-auto w-100">
        <div className="d-flex align-center mb-2">
          <Button
            variant="outline"
            className="d-flex align-center gap-2 btn-md"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={18} />
            Back
          </Button>
        </div>

        <LeadBasicInfo />

        <Card className="lead-summary-card mx-0 mb-4 rounded-20 p-4 max-w-none card-shadow hover-lift position-relative overflow-hidden">
          <div className="lead-summary-header d-flex justify-between align-center gap-3 flex-wrap">
            <div>
              <p className="text-xs text-muted fw-semibold m-0 mb-1">
                LEAD ACTIVITY SUMMARY
              </p>

              <h2 className="text-lg text-primary fw-bold m-0">
                AI Behavioral Insights
              </h2>
            </div>
            <span className="lead-summary-badge text-white">
              Behavioral intelligence
            </span>
          </div>

          <div className="text-md text-slate-600 lh-14 whitespace-pre-line">
            {activitySummary}
          </div>
        </Card>

        <div className="page-header mb-3">
          <div>
            <h1 className="m-0">Lead Activity Analytics</h1>
            <h4 className="text-muted mt-1 m-0">
              Buyer behavior, engagement, and property interest insights
            </h4>
          </div>
        </div>

        <div className="grid-auto-fit mb-4">
          <StatCard
            title="Lead Score"
            value={<ScoreBadge score={selectedLead?.score} />}
            icon={<Flame size={20} />}
            valueColorClass={leadTemperatureClass}
          />
          <StatCard
            title="Preferred Location"
            value={selectedLead?.location || "N/A"}
            icon={<MapPin size={20} />}
          />
          <StatCard
            title="Final Budget"
            value={formatBudget(analytics.buyerIntent?.finalBudget)}
            icon={<IndianRupee size={20} />}
          />
          <StatCard
            title="Chat Widget Duration"
            value={analytics.session?.duration || "N/A"}
            icon={<Clock3 size={20} />}
          />
        </div>

        <SectionCard title="Engagement Overview" className="mb-4">
          <div className="grid-auto-fit">
            <StatCard
              title="Chat Time"
              value={formatDuration(analytics.engagement?.totalChatTime)}
              icon={<MessageSquare size={20} />}
            />
            <StatCard
              title="Projects Time"
              value={formatDuration(analytics.engagement?.totalProjectsTime)}
              icon={<Building2 size={20} />}
            />
            <StatCard
              title="Dominant View"
              value={analytics.engagement?.dominantView || "N/A"}
              icon={<Activity size={20} />}
            />
            <StatCard
              title="Properties Viewed"
              value={analytics.summary?.viewedProperties ?? "0"}
              icon={<Eye size={20} />}
            />
          </div>
        </SectionCard>

        <div className="d-flex flex-wrap gap-2">
          {/* -------------------------------- */}
          {/* LANGUAGE MONITORING */}
          {/* -------------------------------- */}
          {languageMonitoring && (
            <SectionCard
              title="Language Monitoring"
              icon={Languages}
              className="mb-4 flex-grow-1"
            >
              <div className="language-monitoring bg-white p-3 d-flex flex-column gap-3">
                <div className="language-overview">
                  <div className="language-overview-item">
                    <span className="overview-label">Primary Language</span>
                    <span className="overview-value">
                      {languageMonitoring.primary_language}
                    </span>
                  </div>

                  <div className="language-overview-divider" />

                  <div className="language-overview-item">
                    <span className="overview-label">Language Switches</span>
                    <span className="overview-value">
                      {languageMonitoring.switch_count}
                    </span>
                  </div>
                </div>

                <div className="language-timeline">
                  <div className="timeline-title">Conversation Timeline</div>

                  {languageMonitoring.timeline.map((item, index) => (
                    <div key={index} className="timeline-item">
                      <div className="timeline-marker" />
                      <div className="timeline-content">
                        <span className="timeline-language">
                          {item.language}
                        </span>
                        <span className="timeline-duration">
                          {item.duration}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </SectionCard>
          )}
          {/* -------------------------------- */}
          {/* PROPERTY INTEREST */}
          {/* -------------------------------- */}

          <div className="flex-grow-1">
            <SectionCard title="Property Interest" className="h-100">
              {propertyInterest.length ? (
                <div className="property-table-wrapper">
                  <table className="property-table">
                    <thead>
                      <tr>
                        <th>Property</th>
                        <th>Viewed At</th>
                        <th>Status</th>
                      </tr>
                    </thead>

                    <tbody>
                      {propertyInterest.map((property, index) => (
                        <tr key={`${property.property}-${index}`}>
                          <td>
                            <div className="property-name">
                              <Building2 size={18} />
                              <span>{property.property}</span>
                            </div>
                          </td>

                          <td>
                            {formatDateTime(
                              property.viewedAt,
                              "dd/MM/yyyy hh:mm a",
                            )}
                          </td>

                          <td>
                            <span className="view-badge">
                              <Eye size={14} />
                              Viewed
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Card className="p-4">
                  <span className="text-muted">
                    No property interest data available
                  </span>
                </Card>
              )}
            </SectionCard>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
